import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Steps,
  Result,
  Button,
  Divider,
  Spin,
  Image,
  Card as AntCard,
  Table,
  Row,
  Col,
  Tag,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  InfoCircleTwoTone,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faRoute,
  faHome,
  faRoad,
} from "@fortawesome/free-solid-svg-icons";
import { useSubscription, useLazyQuery } from "@apollo/client";
import moment from "moment";
import "moment/locale/es";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import {
  subscriptionCustomerGetOrder,
  recordRoutes,
} from "../../queries/schedulingQueries";
import { useTrackingOrderStyles } from "../styles/TrackingOrderScreenstyles";

export const TrackingOrderScreen = () => {
  const classes = useTrackingOrderStyles();
  const history = useHistory();
  const { token } = useParams();
  const [tokenError, setTokenError] = useState();
  const [tokenDecoded, setTokenDecoded] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [orderNumber, setOrderNumber] = useState("");
  const [orderData, setOrderData] = useState({});
  const [decodedCity, setDecodedCity] = useState("");
  const [orderHistoricalData, setOrderHistoricalData] = useState();
  const [icon, setIcon] = useState();
  const { data, loading, error } = useSubscription(
    subscriptionCustomerGetOrder,
    {
      variables: { orderNumber: orderNumber },
    }
  );
  const [getDataRecordRoutes, dataRecordRoutes] = useLazyQuery(recordRoutes, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (token !== undefined) {
      const verifyToken = async () => {
        try {
          const res = await fetch(process.env.REACT_APP_FUNCTION_VERIFY_TOKEN, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });

          const result = await res.json();

          if (res.status === 200) {
            setTokenDecoded(result.statusText);
            setOrderNumber(result.statusText.orderNumber);
            await getDataRecordRoutes({
              variables: { orderNumber: result.statusText.orderNumber },
            });
          } else {
            setTokenError(result.statusText || "Token inválido");
          }
        } catch (error) {
          setTokenError("Error verificando token");
        }
      };
      verifyToken();
    }
  }, [token, getDataRecordRoutes]);

  useEffect(() => {
    console.log("useEffect ejecutado. orderNumber actual:", orderNumber);
    if (!loading && data && data.routes && data.routes.length > 0) {
      console.log("ESTRUCTURA COMPLETA DEL PEDIDO:", JSON.stringify(data.routes[0].orderTable, null, 2));
      const route = data.routes[0];

      const orderInfo = route.orderTable || {};
      let status = "";
      let startDateTime = "-";
      let endDateTime = "-";
      let startDateRoute = "-";

      switch (route.status) {
        case "none":
        case "start":
          status = "Esperando...";
          break;
        case "started":
          status = "En camino";
          setCurrentStep(2);
          break;
        case "delivered":
          status = "Entregado";
          setCurrentStep(3);
          setIcon(<CheckCircleTwoTone twoToneColor="#52c41a" />);
          break;
        case "undelivered":
          status = "No entregado";
          setCurrentStep(3);
          setIcon(<CloseCircleTwoTone twoToneColor="red" />);
          break;
        case "partial_delivered":
          status = "Parcialmente entregado";
          setCurrentStep(3);
          setIcon(<InfoCircleTwoTone twoToneColor="orange" />);
          break;
        case "rescheduled_delivery":
          status = "Reprogramado";
          setCurrentStep(3);
          setIcon(<InfoCircleTwoTone twoToneColor="orange" />);
          break;
        default:
          status = "Esperando...";
          break;
      }

      if (
        route.endDateTime ||
        route.status === "delivered" ||
        route.status === "undelivered"
      ) {
        setCurrentStep(3);
        if (route.endDateTime)
          endDateTime = moment
            .utc(route.endDateTime)
            .format("DD/MM/YYYY HH:mm");
      } else if (route.startDateTime || route.status === "started") {
        setCurrentStep(2);
        if (route.startDateTime)
          startDateTime = moment
            .utc(route.startDateTime)
            .format("DD/MM/YYYY HH:mm");
      } else if (route.routeBatch?.startDateRoute) {
        setCurrentStep(1);
        startDateRoute = moment
          .utc(route.routeBatch.startDateRoute)
          .format("DD/MM/YYYY HH:mm");
      }

      setOrderData({
        id: route.id,
        consecutiveSaleOrder: orderInfo.consecutiveSaleOrder,
        orderNumber: orderInfo.orderNumber,
        custFullName: orderInfo.custFullName,
        serviceAdvisor: orderInfo.serviceAdvisor,
        address: orderInfo.address,
        createByUserId: route.createByUser?.displayName ?? "",
        destination: orderInfo.destination,
        ordersLines: orderInfo.ordersLines || [],
        createdAt: route.createdAt,
        durationText: route.durationText,
        durationValue: route.durationValue,
        startDateRoute: route.routeBatch?.startDateRoute ?? "",
        startDateTime: route.startDateTime,
        endDateTime: route.endDateTime,
        status,
        reason: route.reason?.description ?? "",
        evidences: route.evidences,
      });
    }
  }, [loading, data, orderNumber, error]);

  useEffect(() => {
    if (
      dataRecordRoutes &&
      dataRecordRoutes.data?.routes &&
      dataRecordRoutes.data?.routes?.length > 0
    ) {
      setOrderHistoricalData(dataRecordRoutes.data?.routes);
    }
  }, [dataRecordRoutes]);

  useEffect(() => {
    if (orderData.destination && orderData.destination.includes(",")) {
      const getCityName = async () => {
        try {
          const [lat, lon] = orderData.destination.split(",");
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat.trim()}&lon=${lon.trim()}`;

          const response = await fetch(url);
          const data = await response.json();

          const city =
            data.address.city ||
            data.address.town ||
            data.address.village ||
            data.address.county ||
            "";
          const state = data.address.state || "";

          setDecodedCity(`${city}`);
        } catch (error) {
          console.log("Error fetching city name:", error);
          setDecodedCity(orderData.destination);
        }
      };
      getCityName();
    } else {
      setDecodedCity(orderData.destination);
    }
  }, [orderData.destination]);

  if (loading)
    return (
      <center
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" />
      </center>
    );
  if (tokenError !== undefined)
    return (
      <div
        style={{
          height: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Result
          status="500"
          title={tokenError}
          extra={[
            <Button
              key="result"
              type="primary"
              shape="round"
              size="large"
              onClick={() => history.replace("/")}
            >
              Aceptar
            </Button>,
          ]}
        />
      </div>
    );

  let deliveryRecord = orderHistoricalData?.find(
    (r) => r.status === "delivered"
  );
  if (!deliveryRecord) {
    deliveryRecord = orderHistoricalData?.find((r) => r.receivedPerson);
  }

  const statusTraduccion = {
    start: "Esperando...",
    started: "En camino",
    delivered: "Entregado",
    undelivered: "No entregado",
    partial_delivered: "Parcialmente entregado",
    rescheduled_delivery: "Reprogramado",
  };

  const titleText =
    orderData.consecutiveSaleOrder ||
    tokenDecoded.consecutiveSaleOrder ||
    orderNumber ||
    "Cargando...";

  const stepItems = [
    {
      title: (
        <FontAwesomeIcon
          size="3x"
          icon={faBuilding}
          color={currentStep === 0 ? "red" : "gray"}
        />
      ),
      subTitle: (
        <small>
          {moment(orderData.createdAt).format("DD/MM/YYYY HH:mm")}
        </small>
      ),
      description: (
        <div>
          <strong>{`Listo en bodega`}</strong>
          <div>
            <small>{`Preparando su pedido`}</small>
          </div>
        </div>
      ),
    },
    {
      title: (
        <FontAwesomeIcon
          size="3x"
          icon={faRoad}
          color={currentStep === 1 ? "red" : "gray"}
        />
      ),
      subTitle: (
        <small>
          {moment.utc(orderData.startDateRoute).format("DD/MM/YYYY HH:mm")}
        </small>
      ),
      description: (
        <div>
          <strong>{`Despachado`}</strong>
          <div>
            <small>{`Su pedido va en camino`}</small>
          </div>
        </div>
      ),
    },
    {
      title: (
        <FontAwesomeIcon
          size="3x"
          icon={faRoute}
          color={currentStep === 2 ? "red" : "gray"}
        />
      ),
      subTitle: (
        <small>
          {moment.utc(orderData.startDateTime).format("DD/MM/YYYY HH:mm")}
        </small>
      ),
      description: (
        <div>
          <strong>{`En ruta`}</strong>
          <div>
            <small>{`Pedido en tramo final`}</small>
          </div>
        </div>
      ),
    },
    {
      title: (
        <FontAwesomeIcon
          size="3x"
          icon={faHome}
          color={currentStep === 3 ? "red" : "gray"}
        />
      ),
      subTitle: (
        <small>{moment.utc(orderData.endDateTime).format("DD/MM/YYYY HH:mm")}
        </small>
      ),
      description: (
        <div>
          <strong>{orderData.status}</strong>
          <div>{icon}</div>
          <div>
            <small style={{ color: "red" }}>{orderData.reason}</small>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        minHeight: window.innerHeight,
        padding: "20px 5%",
        display: "flex",
        justifyContent: "center",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Result
        icon={
          <img
            src="https://res.cloudinary.com/srf/image/upload/v1680285850/NAVITRANS_LOGOS_COLOR_boaoxt.png"
            width="300"
            alt="logo"
          />
        }
        title={`SEGUIMIENTO DEL PEDIDO`}
        subTitle={titleText}
        extra={
          <div style={{ maxWidth: "900px", margin: "0 auto", width: "100%" }}>
            <br />
            <br />
            <br />

            <Steps
              current={currentStep}
              progressDot={(dot) => dot}
              items={stepItems}
              labelPlacement="vertical"
            />

            <br />

            <Accordion className={classes.containerAccordion}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.containerSummary}
              >
                <Typography className={classes.containerHeading}>
                  Detalle de la Entrega
                </Typography>
              </AccordionSummary>

              <AccordionDetails className={classes.containerDetails}>
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>
                      Información de la Orden
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    <Card variant="outlined" className={classes.infoCard}>
                      <CardContent className={classes.infoCardContent}>
                        <Row gutter={[20, 20]}>
                          <Col xs={24} sm={12}>
                            <span className={classes.label}>No. Pedido</span>
                            <span className={classes.value}>{titleText}</span>
                          </Col>
                          <Col xs={24} sm={12}>
                            <span className={classes.label}>No. Orden</span>
                            <span className={classes.value}>
                              {orderData.orderNumber}
                            </span>
                          </Col>
                          <Col xs={24} sm={12}>
                            <span className={classes.label}>
                              Asesor de Ventas
                            </span>
                            <span className={classes.value}>
                              {orderData.serviceAdvisor || "N/D"}
                            </span>
                          </Col>
                          <Col xs={24} sm={12}>
                            <span className={classes.label}>Fecha Entrega</span>
                            <span className={classes.value}>
                              {deliveryRecord?.endDateTime
                                ? moment
                                  .utc(deliveryRecord?.endDateTime)
                                  .format("DD/MM/YYYY h:mm a")
                                : "-"}
                            </span>
                          </Col>
                          <Col span={24}>
                            <span className={classes.label}>Estado Actual</span>
                            <Tag
                              color="blue"
                              style={{
                                marginTop: 4,
                                fontSize: "0.85rem",
                                padding: "6px 14px",
                                borderRadius: "6px",
                                fontWeight: 500,
                                border: "none",
                              }}
                            >
                              {orderData.status || "Cargando..."}
                            </Tag>
                          </Col>
                        </Row>
                      </CardContent>
                    </Card>
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>
                      Información de la Entrega
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    <Card variant="outlined" className={classes.infoCard}>
                      <CardContent className={classes.infoCardContent}>
                        <Row gutter={[20, 20]}>
                          {/* Información del Cliente */}
                          <Col span={24}>
                            <span className={classes.label}>Cliente</span>
                            <span className={classes.value}>
                              {orderData.custFullName || "No disponible"}
                            </span>
                          </Col>
                          <Col span={24}>
                            <span className={classes.label}>Dirección</span>
                            <span className={classes.value}>
                              {orderData.address || "No disponible"}
                            </span>
                          </Col>
                          <Col span={24}>
                            <span className={classes.label}>Ciudad</span>
                            <span className={classes.value}>
                              {decodedCity || "Cargando ubicación..."}
                            </span>
                          </Col>

                          {deliveryRecord && (
                            <>
                              <Col span={24}>
                                <Divider style={{ margin: "20px 0 10px 0" }} />
                              </Col>
                              <Col xs={24} sm={12}>
                                <span className={classes.label}>
                                  Recibido por
                                </span>
                                <span
                                  className={classes.value}
                                  style={{ textTransform: "capitalize" }}
                                >
                                  {deliveryRecord.receivedPerson ||
                                    "No especificado"}
                                </span>
                              </Col>
                              <Col xs={24} sm={12}>
                                <span className={classes.label}>Documento</span>
                                <span className={classes.value}>
                                  {deliveryRecord.receivedDocument ||
                                    "No registrado"}
                                </span>
                              </Col>
                              <Col xs={24} sm={12}>
                                <span className={classes.label}>Estado</span>
                                <Tag
                                  color="blue"
                                  style={{
                                    marginTop: 4,
                                    fontSize: "0.85rem",
                                    padding: "6px 14px",
                                    borderRadius: "6px",
                                    fontWeight: 500,
                                    border: "none",
                                  }}
                                >
                                  {statusTraduccion[deliveryRecord.status] ||
                                    deliveryRecord.status}
                                </Tag>
                              </Col>
                              <Col xs={24} sm={12}>
                                <span className={classes.label}>
                                  Fecha y hora
                                </span>
                                <span className={classes.value}>
                                  {deliveryRecord.endDateTime
                                    ? moment
                                      .utc(deliveryRecord.endDateTime)
                                      .format("DD/MM/YYYY h:mm a")
                                    : "-"}
                                </span>
                              </Col>
                            </>
                          )}
                        </Row>
                      </CardContent>
                    </Card>
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>
                      Artículos de la Entrega
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    {orderData.ordersLines &&
                      orderData.ordersLines.length > 0 ? (
                      <div style={{ width: "100%" }}>
                        {orderData.ordersLines.map((item, index) => (
                          <Card
                            key={item.id || index}
                            variant="outlined"
                            className={classes.infoCard}
                            style={{
                              marginBottom:
                                index === orderData.ordersLines.length - 1
                                  ? 0
                                  : 10,
                            }}
                          >
                            <CardContent className={classes.infoCardContent}>
                              <Row
                                gutter={[10, 10]}
                                align="middle"
                                wrap={false}
                              >
                                <Col
                                  xs={5}
                                  sm={4}
                                  style={{ textAlign: "left" }}
                                >
                                  <span className={classes.label}>Item</span>
                                  <span
                                    className={classes.value}
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {item.productNumber ||
                                      item.id?.toString().slice(-4)}
                                  </span>
                                </Col>

                                <Col
                                  xs={14}
                                  sm={16}
                                  style={{ textAlign: "middle" }}
                                >
                                  <span className={classes.label}>
                                    Producto
                                  </span>
                                  <span
                                    className={classes.value}
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    {item.productName}
                                  </span>
                                </Col>
                                <Col
                                  xs={5}
                                  sm={4}
                                  style={{ textAlign: "right" }}
                                >
                                  <span className={classes.label}>Cant.</span>
                                  <span
                                    className={classes.value}
                                    style={{
                                      fontSize: "0.85rem",
                                      fontWeight: 600,
                                      color: "#555",
                                    }}
                                  >
                                    {item.orderedQuantity}
                                  </span>
                                </Col>
                              </Row>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card variant="outlined" className={classes.infoCard}>
                        <CardContent className={classes.infoCardContent}>
                          <div
                            style={{
                              padding: "20px",
                              textAlign: "center",
                              color: "#999",
                              fontSize: "0.9rem",
                            }}
                          >
                            No hay artículos disponibles
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>
                      Evidencias de la Entrega
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    <Card variant="outlined" className={classes.infoCard}>
                      <CardContent className={classes.infoCardContent}>
                        {orderData.evidences &&
                          orderData.evidences.length > 0 &&
                          orderData.evidences[0].comments &&
                          orderData.evidences[0].comments.length > 0 && (
                            <div style={{ marginBottom: 20, width: "100%" }}>
                              <span className={classes.label}>Comentarios</span>
                              <span className={classes.value}>
                                {orderData.evidences[0].comments}
                              </span>
                            </div>
                          )}

                        {orderData.evidences &&
                          orderData.evidences.length > 0 ? (
                          <Row gutter={[16, 16]} style={{ width: "100%" }}>
                            {orderData.evidences.map((item, index) => (
                              <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                <AntCard
                                  hoverable
                                  style={{
                                    width: "100%",
                                    borderRadius: "10px",
                                    border: "1px solid #e8e8e8",
                                  }}
                                  bodyStyle={{ padding: "12px" }}
                                  cover={
                                    <Image
                                      width="100%"
                                      height={250}
                                      style={{
                                        objectFit:
                                          item.evidenceType === "FIRMA"
                                            ? "contain"
                                            : "cover",
                                      }}
                                      src={item.evidenceURL}
                                      alt={item.evidenceType || "Evidencia"}
                                      preview={{
                                        mask: "Ver imagen",
                                      }}
                                    />
                                  }
                                >
                                  <AntCard.Meta
                                    description={
                                      <Tag
                                        color="cyan"
                                        style={{
                                          width: "100%",
                                          textAlign: "center",
                                          fontSize: "0.8rem",
                                          fontWeight: 500,
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          border: "none",
                                        }}
                                      >
                                        {item.evidenceType || "EVIDENCIA"}
                                      </Tag>
                                    }
                                  />
                                </AntCard>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div
                            style={{
                              padding: "40px 20px",
                              textAlign: "center",
                              color: "#999",
                              fontSize: "0.9rem",
                            }}
                          >
                            No hay evidencias disponibles
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>
          </div>
        }
      />
    </div>
  );
};
